import React, { useState } from "react";
import { Package, ChevronDown, ChevronUp } from "lucide-react";
import { formatCurrency, formatDateTime, formatKey, formatValue } from "../utils/responseParser";

/**
 * OrderCard - Renders order data in a structured card format
 * Handles nested arrays (lineItems, stops, parties) with collapsible sections
 *
 * @param {Object} props
 * @param {Object} props.data - The order data object
 * @param {Function} props.onClick - Optional callback when card is clicked
 * @returns {JSX.Element}
 */
const OrderCard = ({ data, onClick }) => {
    // Collapsible sections state (must be called before any conditional returns)
    const [expandedSections, setExpandedSections] = useState({});

    if (!data || typeof data !== "object") return null;

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    // Status color mapping for orders
    const getStatusColor = (status) => {
        if (!status) return "bg-gray-100 text-gray-700";
        const s = status.toLowerCase();
        if (s.includes("complete") || s.includes("fulfilled")) return "bg-green-100 text-green-700";
        if (s.includes("process") || s.includes("progress") || s.includes("inprogress")) return "bg-blue-100 text-blue-700";
        if (s.includes("pend") || s.includes("planned")) return "bg-amber-100 text-amber-700";
        if (s.includes("cancel")) return "bg-red-100 text-red-700";
        if (s.includes("new")) return "bg-purple-100 text-purple-700";
        return "bg-gray-100 text-gray-700";
    };

    // Stop type badge color
    const getStopTypeColor = (stopType) => {
        if (!stopType) return "bg-gray-100 text-gray-700";
        const s = stopType.toUpperCase();
        if (s === "PICKUP") return "bg-blue-100 text-blue-700";
        if (s === "DELIVERY") return "bg-green-100 text-green-700";
        return "bg-gray-100 text-gray-700";
    };

    // Extract common order fields with fallbacks
    const orderNumber = data.orderNumber || data.order_number || data.orderId || data.order_id || data.id || "N/A";
    const status = data.status || data.orderStatus || data.order_status || "Unknown";
    const poNumber = data.poNumber || data.po_number || data.purchaseOrderNumber || data.purchase_order_number || null;
    const orderType = data.orderType || data.order_type || data.type || null;
    const paymentMethod = data.paymentMethod || data.payment_method || data.paymentType || data.payment_type || null;

    // Financial fields
    const subtotal = data.subtotal || data.subTotal || data.sub_total || null;
    const tax = data.tax || data.taxAmount || data.tax_amount || null;
    const discounts = data.discounts || data.discountAmount || data.discount_amount || null;
    const total = data.total || data.totalAmount || data.total_amount || data.orderTotalAmount || data.order_total_amount || null;
    const currency = data.currency || "USD";

    // Shipping details
    const primaryMode = data.primaryMode || data.primary_mode || data.shippingMode || data.shipping_mode || data.mode || null;
    const primaryCarrier = data.primaryCarrier || data.primary_carrier || data.carrierName || data.carrier_name || data.carrier || null;
    const deliveryMethod = data.deliveryMethod || data.delivery_method || null;
    const shipmentReference = data.shipmentReference || data.shipment_reference || data.shipmentNumber || data.shipment_number || data.shipmentId || data.shipment_id || null;

    // Route information
    const origin = data.origin || data.originLocation || data.origin_location || data.billFromLocation || data.bill_from_location || "-";
    const destination = data.destination || data.destinationLocation || data.destination_location || data.shipToLocation || data.ship_to_location || "-";

    // Parse nested arrays - handle both array and JSON string formats
    const parseArray = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        if (typeof value === "string") {
            try {
                return JSON.parse(value);
            } catch {
                return [];
            }
        }
        return [];
    };

    const lineItems = parseArray(data.lineItems || data.line_items || data.items || data.products);
    const stops = parseArray(data.stops || data.orderStops || data.order_stops);
    const parties = parseArray(data.parties || data.orderParties || data.order_parties);

    // Get additional fields not yet displayed
    const getAdditionalFields = () => {
        const excludeKeys = [
            "orderNumber", "order_number", "orderId", "order_id", "id",
            "status", "orderStatus", "order_status",
            "poNumber", "po_number", "purchaseOrderNumber", "purchase_order_number",
            "orderType", "order_type", "type",
            "paymentMethod", "payment_method", "paymentType", "payment_type",
            "subtotal", "subTotal", "sub_total",
            "tax", "taxAmount", "tax_amount",
            "discounts", "discountAmount", "discount_amount",
            "total", "totalAmount", "total_amount", "orderTotalAmount", "order_total_amount",
            "currency",
            "primaryMode", "primary_mode", "shippingMode", "shipping_mode", "mode",
            "primaryCarrier", "primary_carrier", "carrierName", "carrier_name", "carrier",
            "deliveryMethod", "delivery_method",
            "shipmentReference", "shipment_reference", "shipmentNumber", "shipment_number", "shipmentId", "shipment_id",
            "origin", "originLocation", "origin_location", "billFromLocation", "bill_from_location",
            "destination", "destinationLocation", "destination_location", "shipToLocation", "ship_to_location",
            "lineItems", "line_items", "items", "products",
            "stops", "orderStops", "order_stops",
            "parties", "orderParties", "order_parties",
            "orderDate", "order_date", "date", "createdDate", "created_date"
        ];
        const additional = {};
        for (const [key, value] of Object.entries(data)) {
            if (!excludeKeys.includes(key) && value !== null && value !== undefined) {
                additional[key] = value;
            }
        }
        return additional;
    };

    const additionalFields = getAdditionalFields();

    // Collapsible section component
    const CollapsibleSection = ({ title, count, sectionKey, children }) => {
        const isExpanded = expandedSections[sectionKey];
        return (
            <div className="mt-3 pt-3 border-t border-gray-200">
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        toggleSection(sectionKey);
                    }}
                    className="flex items-center gap-2 w-full text-left"
                >
                    <span className="text-[10px] text-gray-500 uppercase tracking-wide">
                        {title} ({count})
                    </span>
                    {isExpanded ?
                        <ChevronUp className="w-3 h-3 text-gray-400" /> :
                        <ChevronDown className="w-3 h-3 text-gray-400" />
                    }
                </button>
                {isExpanded && <div className="mt-2">{children}</div>}
            </div>
        );
    };

    return (
        <div
            className={`mt-3 rounded-lg border border-gray-200 bg-white overflow-hidden transition-all ${
                onClick ? "cursor-pointer hover:shadow-md hover:border-acs-primary-green" : ""
            }`}
            onClick={() => onClick && onClick(data)}
        >
            {/* Header */}
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                <Package className="w-4 h-4 text-acs-primary-green" />
                <span className="text-sm font-semibold text-gray-800 flex-1">
                    Order {orderNumber}
                </span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getStatusColor(status)}`}>
                    {status}
                </span>
            </div>

            {/* Body */}
            <div className="p-3">
                {/* Route info */}
                <div className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg mb-3">
                    <div className="flex-1">
                        <span className="text-[10px] text-gray-500 uppercase tracking-wide">From</span>
                        <div className="text-sm font-semibold text-gray-800">{origin}</div>
                    </div>
                    <span className="text-xl text-gray-400">→</span>
                    <div className="flex-1">
                        <span className="text-[10px] text-gray-500 uppercase tracking-wide">To</span>
                        <div className="text-sm font-semibold text-gray-800">{destination}</div>
                    </div>
                </div>

                {/* Summary Details */}
                <div className="space-y-1.5 mb-3">
                    {poNumber && (
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-500">PO Number</span>
                            <span className="font-medium text-gray-800">{poNumber}</span>
                        </div>
                    )}
                    {orderType && (
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Order Type</span>
                            <span className="font-medium text-gray-800">{orderType}</span>
                        </div>
                    )}
                    {paymentMethod && (
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Payment Method</span>
                            <span className="font-medium text-gray-800">{paymentMethod}</span>
                        </div>
                    )}
                </div>

                {/* Financial Summary */}
                {(subtotal || tax || discounts || total) && (
                    <div className="p-2.5 bg-gray-50 rounded-lg mb-3">
                        <span className="text-[10px] text-gray-500 uppercase tracking-wide block mb-2">
                            Financial Summary
                        </span>
                        {subtotal != null && (
                            <div className="flex justify-between text-xs mb-1">
                                <span className="text-gray-500">Subtotal</span>
                                <span className="text-gray-700">{formatCurrency(subtotal, currency)}</span>
                            </div>
                        )}
                        {tax != null && (
                            <div className="flex justify-between text-xs mb-1">
                                <span className="text-gray-500">Tax</span>
                                <span className="text-gray-700">{formatCurrency(tax, currency)}</span>
                            </div>
                        )}
                        {discounts != null && (
                            <div className="flex justify-between text-xs mb-1">
                                <span className="text-gray-500">Discounts</span>
                                <span className="text-gray-700">{formatCurrency(discounts, currency)}</span>
                            </div>
                        )}
                        {total != null && (
                            <div className="flex justify-between text-xs pt-1 mt-1 border-t border-gray-200">
                                <span className="font-semibold text-gray-700">Total</span>
                                <span className="font-bold text-acs-primary-green text-sm">
                                    {formatCurrency(total, currency)}
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {/* Shipping Details */}
                {(primaryMode || primaryCarrier || deliveryMethod || shipmentReference) && (
                    <div className="mb-3">
                        <span className="text-[10px] text-gray-500 uppercase tracking-wide block mb-2">
                            Shipping Details
                        </span>
                        <div className="space-y-1.5">
                            {primaryMode && (
                                <div className="flex justify-between text-xs">
                                    <span className="text-gray-500">Mode</span>
                                    <span className="font-medium text-gray-800">{primaryMode}</span>
                                </div>
                            )}
                            {primaryCarrier && (
                                <div className="flex justify-between text-xs">
                                    <span className="text-gray-500">Carrier</span>
                                    <span className="font-medium text-gray-800">{primaryCarrier}</span>
                                </div>
                            )}
                            {deliveryMethod && (
                                <div className="flex justify-between text-xs">
                                    <span className="text-gray-500">Delivery Method</span>
                                    <span className="font-medium text-gray-800">{deliveryMethod}</span>
                                </div>
                            )}
                            {shipmentReference && (
                                <div className="flex justify-between text-xs">
                                    <span className="text-gray-500">Shipment Reference</span>
                                    <span className="font-medium text-acs-primary-green">{shipmentReference}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Line Items Section */}
                {lineItems.length > 0 && (
                    <CollapsibleSection title="Line Items" count={lineItems.length} sectionKey="lineItems">
                        <div className="space-y-2">
                            {lineItems.map((item, idx) => {
                                const sku = item.sku || item.productSku || item.product_sku || item.itemSku || item.item_sku || item.code || "-";
                                const name = item.name || item.itemName || item.item_name || item.productName || item.product_name || item.description || "-";
                                const qty = item.quantity || item.qty || item.orderQuantity || item.order_quantity || "-";
                                const price = item.price || item.unitPrice || item.unit_price || item.itemPrice || item.item_price || null;
                                const total = item.total || item.lineTotal || item.line_total || null;

                                return (
                                    <div key={idx} className="p-2 bg-gray-50 rounded text-xs">
                                        <div className="flex items-start gap-2">
                                            <span className="font-semibold text-gray-800 min-w-[60px]">{sku}</span>
                                            <span className="flex-1 text-gray-700">{name}</span>
                                            <span className="text-gray-600">x{qty}</span>
                                        </div>
                                        {(price || total) && (
                                            <div className="flex justify-end gap-4 mt-1 text-gray-600">
                                                {price && <span>@ {formatCurrency(price, currency)}</span>}
                                                {total && <span className="font-medium text-gray-800">= {formatCurrency(total, currency)}</span>}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </CollapsibleSection>
                )}

                {/* Stops Section */}
                {stops.length > 0 && (
                    <CollapsibleSection title="Stops" count={stops.length} sectionKey="stops">
                        <div className="space-y-2">
                            {stops.map((stop, idx) => {
                                const stopType = stop.stopType || stop.stop_type || stop.type || "-";
                                const locationName = stop.locationName || stop.location_name || stop.location || stop.facilityName || stop.facility_name || "-";
                                const address = stop.address || stop.streetAddress || stop.street_address || stop.locationAddress || stop.location_address || null;
                                const scheduledGateIn = stop.scheduledGateIn || stop.scheduled_gate_in || stop.scheduledArrival || stop.scheduled_arrival || null;
                                const scheduledGateOut = stop.scheduledGateOut || stop.scheduled_gate_out || stop.scheduledDeparture || stop.scheduled_departure || null;

                                return (
                                    <div key={idx} className="p-2 bg-gray-50 rounded">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className={`text-[10px] font-medium px-2 py-0.5 rounded ${getStopTypeColor(stopType)}`}>
                                                {stopType}
                                            </span>
                                            <span className="font-semibold text-gray-800 text-xs">{locationName}</span>
                                        </div>
                                        {address && (
                                            <div className="text-xs text-gray-600 mb-1">{address}</div>
                                        )}
                                        <div className="flex gap-3 text-xs text-gray-500">
                                            {scheduledGateIn && <span>In: {formatDateTime(scheduledGateIn)}</span>}
                                            {scheduledGateOut && <span>Out: {formatDateTime(scheduledGateOut)}</span>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CollapsibleSection>
                )}

                {/* Parties Section */}
                {parties.length > 0 && (
                    <CollapsibleSection title="Parties" count={parties.length} sectionKey="parties">
                        <div className="space-y-2">
                            {parties.map((party, idx) => {
                                const partyType = party.partyType || party.party_type || party.type || "-";
                                const companyName = party.companyName || party.company_name || party.name || party.company || "-";
                                const contact = party.contactName || party.contact_name || party.contact || party.contactPerson || party.contact_person || null;
                                const email = party.email || party.emailAddress || party.email_address || null;
                                const phone = party.phone || party.phoneNumber || party.phone_number || null;

                                return (
                                    <div key={idx} className="p-2 bg-gray-50 rounded">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className="text-[10px] font-semibold px-2 py-0.5 bg-purple-50 text-purple-700 rounded">
                                                {partyType}
                                            </span>
                                            <span className="font-medium text-gray-800 text-xs">{companyName}</span>
                                        </div>
                                        {(contact || email || phone) && (
                                            <div className="text-xs text-gray-600">
                                                {contact && <div className="mb-0.5">Contact: {contact}</div>}
                                                {email && <div className="mb-0.5">Email: {email}</div>}
                                                {phone && <div>Phone: {phone}</div>}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </CollapsibleSection>
                )}

                {/* Additional fields */}
                {Object.keys(additionalFields).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200 space-y-1">
                        <span className="text-[10px] text-gray-500 uppercase tracking-wide block mb-2">
                            Additional Information
                        </span>
                        {Object.entries(additionalFields).map(([key, value]) => (
                            <div key={key} className="flex justify-between text-xs">
                                <span className="text-gray-500">{formatKey(key)}</span>
                                <span className="font-medium text-gray-800">{formatValue(value)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrderCard;
